'use client'

import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { CurrentClientContext } from '@tebuto/guards/current-client-context'
import { ComponentSpinner } from '@tebuto/layout/Spinner'
import { StripeCheckoutSessionReloadPrompt } from '@tebuto/payment/checkout'
import PayPalCheckout from '@tebuto/payment/paypal/PayPalCheckout'
import StripeCheckout, { type StripeCheckoutSubmitState } from '@tebuto/payment/stripe/StripeCheckout'
import type { WidgetOnlinePaymentMethod } from '@tebuto/payment/stripe/stripe-payment-method-mapping'
import {
    buildStripePaymentElementOptionsForMethods,
    buildStripeWidgetAppearance
} from '@tebuto/payment/stripe/stripe-widget-appearance'
import type { WidgetStripeCheckoutSessionState } from '@tebuto/payment/stripe/use-widget-stripe-checkout-session'
import { useTheme } from '@tebuto/widget/theme/ThemeProvider'
import { useCallback, useContext, useMemo } from 'react'
import { toast } from 'react-toastify'
import { BookingContext } from '../context'
import { resolveCheckoutCustomerFields } from '../functions'
import type { EventLocationSelection, OnlinePaymentMethods } from '../types'
import { PaymentType } from '../types'
import { clearPendingCheckout } from './pending-checkout-storage'

const STRIPE_PAYMENT_FORM_MIN_HEIGHT_PX = 140

interface NativeOnlinePaymentActionProps {
    onlineProviderType?: 'paypal' | 'stripe' | null
    onlinePaymentMethods: OnlinePaymentMethods
    stripeConnectedAccountId?: string | null
    stripePublishableKey?: string | null
    stripeCheckoutSession: WidgetStripeCheckoutSessionState
    setProcessing: (processing: boolean) => void
    isProcessing: boolean
    onStripeSubmitStateChange?: (state: StripeCheckoutSubmitState | null) => void
    offlineMethodSelected?: boolean
    onStripePaymentElementChange?: (event: { collapsed?: boolean }) => void
}

export function NativeOnlinePaymentAction({
    onlineProviderType,
    onlinePaymentMethods,
    stripeConnectedAccountId,
    stripePublishableKey,
    stripeCheckoutSession,
    setProcessing,
    isProcessing,
    onStripeSubmitStateChange,
    offlineMethodSelected = false,
    onStripePaymentElementChange
}: NativeOnlinePaymentActionProps) {
    if (onlineProviderType === 'stripe') {
        if (!stripePublishableKey || !stripeConnectedAccountId) {
            return <p className="text-sm text-red-600">Stripe ist nicht konfiguriert.</p>
        }

        return (
            <StripePaymentAction
                stripeConnectedAccountId={stripeConnectedAccountId}
                stripePublishableKey={stripePublishableKey}
                stripeCheckoutSession={stripeCheckoutSession}
                onlinePaymentMethods={onlinePaymentMethods}
                setProcessing={setProcessing}
                onSubmitStateChange={onStripeSubmitStateChange}
                offlineMethodSelected={offlineMethodSelected}
                onPaymentElementChange={onStripePaymentElementChange}
            />
        )
    }

    if (onlineProviderType === 'paypal') {
        return (
            <PayPalPaymentAction
                onlinePaymentMethods={onlinePaymentMethods}
                setProcessing={setProcessing}
                isProcessing={isProcessing}
            />
        )
    }

    return null
}

function StripePaymentAction({
    stripeConnectedAccountId,
    stripePublishableKey,
    stripeCheckoutSession,
    onlinePaymentMethods,
    setProcessing,
    onSubmitStateChange,
    offlineMethodSelected = false,
    onPaymentElementChange
}: {
    stripeConnectedAccountId: string
    stripePublishableKey: string
    stripeCheckoutSession: WidgetStripeCheckoutSessionState
    onlinePaymentMethods: OnlinePaymentMethods
    setProcessing: (processing: boolean) => void
    onSubmitStateChange?: (state: StripeCheckoutSubmitState | null) => void
    offlineMethodSelected?: boolean
    onPaymentElementChange?: (event: { collapsed?: boolean }) => void
}) {
    const { t } = useComponentTranslation()
    const { theme } = useTheme()
    const {
        categories,
        bookingEvent,
        bookingData,
        useCreateCheckoutSession,
        useBookEvent,
        setBookingResult,
        setState,
        setNavigationDirection
    } = useContext(BookingContext)

    const { isLoading: isCreatingSession } = useCreateCheckoutSession()
    const { bookEvent, isLoading: isBookingEventLoading } = useBookEvent()

    const { clientSecret, checkoutSessionId, returnUrl, isInitializing, initError, reloadCheckoutSession } =
        stripeCheckoutSession

    const category = useMemo(
        () => (bookingEvent ? categories[bookingEvent.categoryId] : null),
        [bookingEvent, categories]
    )

    const stripeAppearance = useMemo(
        () => buildStripeWidgetAppearance(theme, { offlineMethodSelected }),
        [theme, offlineMethodSelected]
    )
    const paymentElementOptions = useMemo(
        () => buildStripePaymentElementOptionsForMethods(onlinePaymentMethods as WidgetOnlinePaymentMethod[]),
        [onlinePaymentMethods]
    )

    const handleSuccess = useCallback(
        async (confirmedCheckoutSessionId: string): Promise<void> => {
            if (!bookingEvent || !bookingData) {
                throw new Error('Booking event not found')
            }

            const booking = await bookEvent({
                event: bookingEvent,
                ...bookingData,
                selectedLocation: bookingData.selectedLocation as unknown as EventLocationSelection,
                paymentType: PaymentType.ONLINE,
                orderId: confirmedCheckoutSessionId
            })

            setBookingResult(booking)
            clearPendingCheckout()
            setState('success')
            setNavigationDirection('forward')
            setProcessing(false)
        },
        [bookEvent, bookingEvent, bookingData, setBookingResult, setState, setNavigationDirection, setProcessing]
    )

    if (!bookingEvent || !category) {
        return null
    }

    if (initError) {
        return (
            <StripeCheckoutSessionReloadPrompt
                message={initError}
                onReload={reloadCheckoutSession}
                isReloading={isInitializing}
            />
        )
    }

    if (isInitializing || !clientSecret || !checkoutSessionId) {
        return (
            <div
                className="flex flex-col items-center justify-center gap-2"
                style={{ minHeight: STRIPE_PAYMENT_FORM_MIN_HEIGHT_PX }}
                aria-busy="true"
            >
                <ComponentSpinner className="size-6 widget-primary-600" />
                <p className="text-sm widget-text-secondary">{t('booking.payment.stripeLoading')}</p>
            </div>
        )
    }

    const paymentElementRemountKey = onlinePaymentMethods.join('-')

    return (
        <StripeCheckout
            clientSecret={clientSecret}
            stripeConnectedAccountId={stripeConnectedAccountId}
            publishableKey={stripePublishableKey}
            returnUrl={returnUrl}
            onSuccess={handleSuccess}
            isSubmitting={isCreatingSession || isBookingEventLoading}
            showToastOnError={false}
            loadingLabel={t('booking.payment.stripeLoading')}
            appearance={stripeAppearance}
            paymentElementOptions={paymentElementOptions}
            paymentElementRemountKey={paymentElementRemountKey}
            useWidgetButton
            showSubmitButton={false}
            onSubmitStateChange={onSubmitStateChange}
            onPaymentElementChange={onPaymentElementChange}
            onReloadSession={reloadCheckoutSession}
            reloadSessionLabel={t('booking.payment.reloadCheckoutSession')}
        />
    )
}

function PayPalPaymentAction({
    onlinePaymentMethods,
    setProcessing,
    isProcessing
}: {
    onlinePaymentMethods: OnlinePaymentMethods
    setProcessing: (processing: boolean) => void
    isProcessing: boolean
}) {
    const {
        categories,
        bookingEvent,
        bookingData,
        useGeneratePayPalClientToken,
        useCreateCheckoutOrder,
        useBookEvent,
        setBookingResult,
        setState,
        setNavigationDirection
    } = useContext(BookingContext)
    const clientWhoAmI = useContext(CurrentClientContext)

    const {
        data: tokenData,
        showLoading: tokenLoading,
        error: tokenError,
        mutate: mutateToken
    } = useGeneratePayPalClientToken()
    const { createCheckoutOrder, isLoading: isCreatingOrder } = useCreateCheckoutOrder()
    const { bookEvent, isLoading: isBookingEventLoading } = useBookEvent()

    const isLoading = tokenLoading || isCreatingOrder || isBookingEventLoading || isProcessing

    const category = useMemo(
        () => (bookingEvent ? categories[bookingEvent.categoryId] : null),
        [bookingEvent, categories]
    )

    const handleCreateOrder = useCallback(async (): Promise<string> => {
        setProcessing(true)
        try {
            if (!bookingEvent || !bookingData) {
                throw new Error('Booking event not found')
            }

            const customer = resolveCheckoutCustomerFields(bookingData, clientWhoAmI)
            const response = await createCheckoutOrder({
                event: bookingEvent,
                ...customer
            })
            return response.id
        } catch (error) {
            toast.error('Fehler beim Erstellen der Bestellung. Bitte versuchen Sie es erneut.')
            setProcessing(false)
            throw error
        }
    }, [createCheckoutOrder, bookingEvent, bookingData, clientWhoAmI, setProcessing])

    const handleApprove = useCallback(
        async ({ orderID }: { orderID: string }): Promise<void> => {
            if (!bookingEvent || !bookingData) {
                throw new Error('Booking event not found')
            }

            const booking = await bookEvent({
                event: bookingEvent,
                ...bookingData,
                selectedLocation: bookingData.selectedLocation as unknown as EventLocationSelection,
                paymentType: PaymentType.ONLINE,
                orderId: orderID
            })

            setBookingResult(booking)
            clearPendingCheckout()
            setState('success')
            setNavigationDirection('forward')
            setProcessing(false)
        },
        [bookEvent, bookingEvent, bookingData, setBookingResult, setState, setNavigationDirection, setProcessing]
    )

    const handleError = useCallback(() => {
        setProcessing(false)
    }, [setProcessing])

    if (!bookingEvent || !category) {
        return null
    }

    if (tokenError) {
        return (
            <button
                type="button"
                onClick={() => mutateToken()}
                className="w-full rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 transition-colors hover:bg-red-100"
            >
                Zahlung laden fehlgeschlagen – Klicken zum Wiederholen
            </button>
        )
    }

    if (tokenLoading || !tokenData) {
        return (
            <div
                className="flex min-h-11 w-full items-center justify-center rounded-lg border widget-border widget-bg-field"
                aria-busy="true"
            >
                <ComponentSpinner className="size-5 widget-primary-600" />
            </div>
        )
    }

    return (
        <PayPalCheckout
            payPalClientToken={tokenData.token}
            onlinePaymentMethods={onlinePaymentMethods}
            pricing={{
                grossPrice: category.price,
                taxRate: category.taxRate,
                label: category.name,
                date: bookingEvent.start,
                isOutage: false,
                outageFeeApplies: 'no',
                outageFeePrice: null
            }}
            handleCreateOrder={handleCreateOrder}
            handleApprove={handleApprove}
            handleError={handleError}
            isPayPalLoading={isLoading}
            useWidgetButton
        />
    )
}
